import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import PollAvailabilityGrid from './PollAvailabilityGrid';

// ─── Types ────────────────────────────────────────────────────────────────────

interface CourseRound { course_id: number; rounds: number; }

interface ScheduleConfig {
  config_id?: number;
  total_rounds: 2 | 3 | 4;
  meet_everyone_once: boolean;
  leaderboard_mode: boolean;
  course_rounds?: CourseRound[];
  dynamic_scheduling?: boolean;
  announce_chat_ids?: string[];
  announce_batch_minutes?: number;
}

interface PollSlot {
  slot_id: number;
  slot_date: string;
  am_pm: 'AM' | 'PM';
  vote_count: number;
  my_vote: boolean;
  my_notes: string | null;
}

interface PollInvitation {
  player_id: number;
  first_name: string;
  last_name: string;
  league_status?: string;
  voted: boolean;
  vote_count: number;
  voted_slot_ids?: number[];
  notes?: string | null;
}

// A player taken off the poll whose availability picks are still on file —
// they come straight back if the player is invited again.
interface SavedVotes {
  player_id: number;
  first_name: string;
  last_name: string;
  vote_count: number;
}

interface Poll {
  poll_id: number;
  status: 'open' | 'closed';
  slots: PollSlot[];
  invitations: PollInvitation[];
  saved_votes?: SavedVotes[];
}

interface RoundReservation {
  reservation_id: number;
  reservation_date: string | null;
  tee_time: string | null;
  reservation_course_name: string | null;
  num_players: number | null;
}

interface ScheduleRound {
  sched_round_id?: number;
  round_number: number;
  slot_id: number | null;
  slot_date: string | null;
  am_pm: 'AM' | 'PM' | null;
  course_id: number | null;
  course_name: string | null;
  suggested_course_id?: number | null;
  suggested_course_name?: string | null;
  blocked_courses?: { course_id: number; course_name: string | null }[];
  is_leaderboard_round?: boolean;
  is_final_round?: boolean;
  reservations: RoundReservation[];
}

interface PerPlayerEvent {
  round_number: number;
  group_number: number;
  slot_date: string | null;
  am_pm: 'AM' | 'PM' | null;
  course_name: string | null;
  suggested_course_id?: number | null;
  suggested_course_name?: string | null;
  is_leaderboard_round: boolean;
  tee_time?: string | null;
  reservation_name?: string | null;
  complex_name?: string | null;
}

interface PerPlayerSchedule {
  player_id: number;
  first_name: string;
  last_name: string;
  events: PerPlayerEvent[];
  rounds_assigned: number;
}

interface GroupPlayer { player_id: number; first_name: string; last_name: string; }

interface ScheduleGroup {
  group_id: number;
  round_number: number;
  group_number: number;
  slot_date: string | null;
  am_pm: 'AM' | 'PM' | null;
  course_name: string | null;
  // Course the generator picked for THIS group when the round has none pinned.
  // Groups in the same slot can carry different courses.
  suggested_course_id?: number | null;
  suggested_course_name?: string | null;
  is_leaderboard_round: boolean;
  reservation_id?: number | null;
  tee_time?: string | null;
  reservation_name?: string | null;
  complex_name?: string | null;
  reservation_course_name?: string | null;
  players: GroupPlayer[];
}

interface ReservationSummaryItem {
  date: string;
  am_pm: 'AM' | 'PM';
  course_name: string | null;
  is_suggested?: boolean;
  group_count: number;
}

interface ActivityEntry {
  activity_id: number;
  kind: 'vote_change' | 'roster_change' | 'rebuild' | 'batch_open' | 'announce' | 'skipped';
  status: 'ok' | 'failed' | 'skipped';
  detail: string | null;
  message: string | null;
  chat_ids: string[] | null;
  created_at: string;
}

interface DynamicStatus {
  enabled: boolean;
  locked: boolean;
  today: string;
  timezone: string;
  batch_minutes: number;
  whatsapp_connected: boolean;
  announce_chat_count: number;
  last_generated_at: string | null;
  schedule_status: 'draft' | 'published' | null;
  pending_batch: {
    opened_at: string; fires_at: string; change_count: number;
    changes: { playerName: string; clause: string }[];
  } | null;
  activity: ActivityEntry[];
}

interface SchedulerDiagnostic {
  severity: 'error' | 'warning' | 'info';
  message: string;
}

// A player still in an upcoming group after being taken off the poll: the
// schedule was built before the roster changed.
interface UninvitedScheduled {
  player_id: number;
  first_name: string;
  last_name: string;
  events: string[];
}

interface Schedule {
  schedule_id: number;
  status: 'draft' | 'published';
  warnings: SchedulerDiagnostic[];
  generated_at: string;
  groups: ScheduleGroup[];
  reservation_summary: ReservationSummaryItem[];
  per_player_schedule?: PerPlayerSchedule[];
  public_url?: string | null;
  uninvited_players?: UninvitedScheduled[];
}

interface TournamentStanding {
  rank: number;
  player_id: number;
  first_name: string;
  last_name: string;
  total_score: number;
  rounds_played: number;
  strokes_back: number | null;
}

interface SchedulerData {
  config: ScheduleConfig | null;
  poll: Poll | null;
  rounds: ScheduleRound[];
  schedule: Schedule | null;
  tournament_standings: TournamentStanding[];
}

interface PlayerOption { player_id: number; first_name: string; last_name: string; league_status?: string; }
interface Course { course_id: number; course_name: string; facility_name?: string | null; }
interface ReservationOption {
  reservation_id: number;
  course_name: string | null;
  reservation_date: string | null;
  tee_time: string | null;
  num_players: number | null;
  status: string;
}

interface Props {
  tournamentId: number;
  tournamentName: string;
  startDate?: string | null;
  endDate?: string | null;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function datesBetween(start: string | null | undefined, end: string | null | undefined): string[] {
  if (!start || !end) return [];
  const s = start.length >= 10 ? start.slice(0, 10) : start;
  const e = end.length >= 10 ? end.slice(0, 10) : end;
  const m1 = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const m2 = e.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m1 || !m2) return [];
  const cur = new Date(+m1[1], +m1[2] - 1, +m1[3]);
  const stop = new Date(+m2[1], +m2[2] - 1, +m2[3]);
  if (isNaN(cur.getTime()) || isNaN(stop.getTime())) return [];
  const out: string[] = [];
  while (cur <= stop) {
    out.push(`${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}-${String(cur.getDate()).padStart(2, '0')}`);
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

function fmtDate(d: string | null | undefined) {
  if (!d) return '—';
  const ds = d.length >= 10 ? d.slice(0, 10) : d;
  const m = ds.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return d;
  const dt = new Date(+m[1], +m[2] - 1, +m[3]);
  if (isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' });
}

// Renders AM/PM as "Morning" / "Afternoon" so "Fri Jun 5 PM" can't be misread as 5 PM.
function fmtSession(amPm: 'AM' | 'PM' | null | undefined) {
  if (!amPm) return '—';
  return amPm === 'AM' ? 'Morning' : 'Afternoon';
}

// A course the generator picked for a group whose round has no course pinned.
// Yellow so it reads as "proposed, not booked" — the same slot can carry two of
// these on different courses when its groups split to satisfy the course plan.
// One chip with two states, so a course visibly graduates from a proposal to a
// booking in place rather than silently swapping for plain text:
//   suggested — amber, dashed, lightbulb: the generator's pick, nothing booked
//   confirmed — green, solid, tick: pinned on the round or backed by a tee time
const CourseChip: React.FC<{ name: string; confirmed?: boolean; compact?: boolean }> =
  ({ name, confirmed, compact }) => (
    <div
      title={confirmed
        ? 'Confirmed course — a tee time is booked for this group'
        : 'Suggested course — confirm by linking a reservation for this course'}
      style={{
        display: 'inline-flex', alignItems: 'flex-start', gap: 4,
        fontSize: compact ? 10 : 11, fontWeight: 600, lineHeight: 1.35,
        color: confirmed ? '#0a3622' : '#7a5c00',
        background: confirmed ? '#d1e7dd' : '#fff8e1',
        border: confirmed ? '1px solid #a3cfbb' : '1px dashed #e0b400',
        borderRadius: 4, padding: compact ? '1px 5px' : '2px 6px',
        marginBottom: compact ? 0 : 4, maxWidth: '100%',
      }}
    >
      <span aria-hidden="true">{confirmed ? '✓' : '💡'}</span>
      {/* Wrap rather than ellipsis: a course truncated to "Irelan…" tells the
          reader nothing, and these sit in narrow group cards. */}
      <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{name}</span>
    </div>
  );

// ─── Component ───────────────────────────────────────────────────────────────

const TournamentSchedulerPanel: React.FC<Props> = ({ tournamentId, tournamentName, startDate, endDate }) => {
  const { getAccessTokenSilently } = useAuth0();
  const APIURL = process.env.REACT_APP_API_URL;

  const [activeTab, setActiveTab] = useState<'config' | 'poll' | 'rounds' | 'schedule'>('config');
  const [data, setData] = useState<SchedulerData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [allPlayers, setAllPlayers] = useState<PlayerOption[]>([]);
  const [allCourses, setAllCourses] = useState<Course[]>([]);
  const [reservations, setReservations] = useState<ReservationOption[]>([]);

  // Config
  const [cfgTotalRounds, setCfgTotalRounds] = useState<2 | 3 | 4>(4);
  const [cfgMeetEveryone, setCfgMeetEveryone] = useState(false);
  const [cfgCourseRounds, setCfgCourseRounds] = useState<CourseRound[]>([]);
  const [cfgDynamic, setCfgDynamic] = useState(false);
  const [cfgAnnounceChats, setCfgAnnounceChats] = useState<string[]>([]);
  const [cfgBatchMinutes, setCfgBatchMinutes] = useState(15);
  const [dynStatus, setDynStatus] = useState<DynamicStatus | null>(null);
  const [dynBusy, setDynBusy] = useState(false);
  const [announceChatOptions, setAnnounceChatOptions] = useState<{ id: string; name: string }[]>([]);
  const [announceChatsError, setAnnounceChatsError] = useState<string | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);

  // Poll
  const [newSlots, setNewSlots] = useState<{ slot_date: string; am_pm: 'AM' | 'PM' }[]>([]);
  const [newSlotDate, setNewSlotDate] = useState('');
  const [newSlotAmPm, setNewSlotAmPm] = useState<'AM' | 'PM'>('AM');
  const [inviteIds, setInviteIds] = useState<number[]>([]);
  const [inviteSearch, setInviteSearch] = useState('');
  const [inviteStatusFilter, setInviteStatusFilter] = useState<'all' | 'Member' | 'Guest'>('all');
  const [myVotedSlots, setMyVotedSlots] = useState<number[]>([]);
  const [myNotes, setMyNotes] = useState('');

  // Player responses – per-player draft slots for auto-save (optimistic)
  const [playerDraftSlots, setPlayerDraftSlots] = useState<Record<number, number[]>>({});
  const [playerSaving, setPlayerSaving] = useState<number | null>(null);

  // PDF export + WhatsApp
  const scheduleRef = useRef<HTMLDivElement>(null);
  const [waModal, setWaModal] = useState(false);
  const [waGroups, setWaGroups] = useState<{ id: string; name: string }[]>([]);
  const [waGroupId, setWaGroupId] = useState('');
  const [waCaption, setWaCaption] = useState('');
  const [waSending, setWaSending] = useState(false);
  const [waError, setWaError] = useState<string | null>(null);

  // Events
  const [roundAssignments, setRoundAssignments] = useState<{
    slot_id: number; slot_date: string; am_pm: 'AM' | 'PM'; vote_count: number;
    course_id: number | null; is_leaderboard_round: boolean; is_final_round: boolean;
    reservation_ids: number[];
  }[]>([]);
  // slot_id → whether to show the pending "Add Link" dropdown row
  const [showAddResvRow, setShowAddResvRow] = useState<Record<number, boolean>>({});

  // Poll auto-save
  const voteTimerRef = React.useRef<ReturnType<typeof setTimeout>>();
  const hasUserVoteChange = React.useRef(false);

  // Rounds auto-save
  const roundsTimerRef = React.useRef<ReturnType<typeof setTimeout>>();
  const hasRoundsChange = React.useRef(false);
  const [roundsSaving, setRoundsSaving] = useState(false);
  const [diagLoading, setDiagLoading] = useState(false);

  // Optimistic local overrides for group reservation assignments.
  // Updated immediately on selection so other dropdowns filter instantly,
  // without waiting for the server round-trip.
  const [localGroupResvs, setLocalGroupResvs] = useState<Record<number, number | null>>({});

  const authFetch = useCallback(
    async (url: string, opts: RequestInit = {}) => {
      const token = await getAccessTokenSilently();
      return fetch(url, {
        ...opts,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(opts.headers || {}) },
      });
    },
    [getAccessTokenSilently]
  );

  const load = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    try {
      const [sched, players, courses, resvs] = await Promise.all([
        authFetch(`${APIURL}/tournaments/${tournamentId}/scheduler`).then((r) => r.json()),
        authFetch(`${APIURL}/players`).then((r) => r.json()),
        authFetch(`${APIURL}/coursesv2`).then((r) => r.json()),
        authFetch(`${APIURL}/reservations/list`).then((r) => r.json()).catch(() => []),
      ]);

      setData(sched);
      setAllPlayers(players || []);
      setAllCourses(courses || []);
      setReservations(Array.isArray(resvs) ? resvs : []);

      if (sched.config) {
        setCfgTotalRounds(sched.config.total_rounds ?? 4);
        setCfgMeetEveryone(sched.config.meet_everyone_once ?? false);
        setCfgCourseRounds(Array.isArray(sched.config.course_rounds) ? sched.config.course_rounds : []);
        setCfgDynamic(!!sched.config.dynamic_scheduling);
        setCfgAnnounceChats(Array.isArray(sched.config.announce_chat_ids) ? sched.config.announce_chat_ids : []);
        setCfgBatchMinutes(Number(sched.config.announce_batch_minutes ?? 15));
      }

      if (sched.poll) {
        const myVotes = (sched.poll.slots as PollSlot[]).filter((s) => s.my_vote).map((s) => s.slot_id);
        hasUserVoteChange.current = false;
        setMyVotedSlots(myVotes);
        const notesSlot = (sched.poll.slots as PollSlot[]).find((s) => s.my_notes);
        setMyNotes(notesSlot?.my_notes || '');
        setInviteIds((sched.poll.invitations as PollInvitation[]).map((i) => i.player_id));
      }

      const savedBySlot: Record<number, ScheduleRound> = {};
      for (const r of (sched.rounds || []) as ScheduleRound[]) {
        if (r.slot_id) savedBySlot[r.slot_id] = r;
      }
      hasRoundsChange.current = false;
      const pollSlotList: PollSlot[] = sched.poll?.slots || [];
      setRoundAssignments(pollSlotList.map((slot) => {
        const saved = savedBySlot[slot.slot_id];
        return {
          slot_id: slot.slot_id, slot_date: slot.slot_date, am_pm: slot.am_pm,
          vote_count: slot.vote_count,
          course_id: saved?.course_id ?? null,
          is_leaderboard_round: saved?.is_leaderboard_round ?? false,
          is_final_round: saved?.is_final_round ?? false,
          reservation_ids: saved ? (saved.reservations || []).map((rr) => rr.reservation_id) : [],
        };
      }));
    } catch (e) {
      console.error('[scheduler load]', e);
    } finally {
      setLoading(false);
    }
  }, [APIURL, tournamentId, authFetch]);

  useEffect(() => { load(); }, [load]);


  // When the Schedule tab becomes active, scroll the schedule content into view.
  useEffect(() => {
    if (activeTab !== 'schedule') return;
    // Defer to after layout so the tab content has mounted and the ref is attached.
    const id = requestAnimationFrame(() => {
      scheduleRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    return () => cancelAnimationFrame(id);
  }, [activeTab]);

  useEffect(() => {
    if (!hasUserVoteChange.current) return;
    if (!poll || poll.status !== 'open') return;
    clearTimeout(voteTimerRef.current);
    voteTimerRef.current = setTimeout(() => {
      authFetch(`${APIURL}/tournaments/${tournamentId}/scheduler/poll/vote`, {
        method: 'POST',
        body: JSON.stringify({ slot_ids: myVotedSlots, notes: myNotes || null }),
      }).catch((e) => console.error('Auto-save vote', e));
    }, 700);
    return () => clearTimeout(voteTimerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myVotedSlots]);

  // Auto-save Rounds tab edits after a short debounce.
  useEffect(() => {
    if (!hasRoundsChange.current) return;
    clearTimeout(roundsTimerRef.current);
    roundsTimerRef.current = setTimeout(async () => {
      setRoundsSaving(true);
      try {
        await authFetch(`${APIURL}/tournaments/${tournamentId}/scheduler/rounds`, {
          method: 'PUT',
          body: JSON.stringify({ rounds: roundAssignments }),
        });
        await load({ silent: true });
      } catch (e) {
        console.error('Auto-save rounds', e);
      } finally {
        setRoundsSaving(false);
      }
    }, 600);
    return () => clearTimeout(roundsTimerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roundAssignments]);

  // Load the WhatsApp groups the bot can post to, for the announcement target.
  const loadAnnounceChats = async () => {
    setAnnounceChatsError(null);
    try {
      const token = await getAccessTokenSilently();
      const res = await fetch(`${APIURL}/whatsapp/chats`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('failed');
      setAnnounceChatOptions((await res.json()) || []);
    } catch {
      setAnnounceChatsError('Could not load WhatsApp groups. Is the bot connected?');
    }
  };

  const loadDynamicStatus = React.useCallback(async () => {
    try {
      const res = await authFetch(`${APIURL}/tournaments/${tournamentId}/scheduler/dynamic-status`);
      if (res.ok) setDynStatus(await res.json());
    } catch { /* status is diagnostic only — never block the page on it */ }
  }, [APIURL, tournamentId, authFetch]);

  // Keep the dynamic panel current: refresh on load, then poll while enabled so
  // a pending batch's countdown and the activity log stay live.
  useEffect(() => { loadDynamicStatus(); }, [loadDynamicStatus, data?.config?.dynamic_scheduling]);
  useEffect(() => {
    if (!data?.config?.dynamic_scheduling) return;
    const id = setInterval(loadDynamicStatus, 20000);
    return () => clearInterval(id);
  }, [data?.config?.dynamic_scheduling, loadDynamicStatus]);

  // Fire the collected changes now instead of waiting for the batch window.
  const announceNow = async () => {
    setDynBusy(true);
    try {
      const res = await authFetch(`${APIURL}/tournaments/${tournamentId}/scheduler/announce-now`, { method: 'POST' });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) alert(body.error || 'Failed to send the announcement.');
      await loadDynamicStatus();
    } finally { setDynBusy(false); }
  };

  // ── Config ─────────────────────────────────────────────────────────────────
  const saveConfig = async () => {
    setSaving(true);
    try {
      await authFetch(`${APIURL}/tournaments/${tournamentId}/scheduler/config`, {
        method: 'PUT',
        body: JSON.stringify({
          total_rounds: cfgTotalRounds,
          meet_everyone_once: cfgMeetEveryone,
          leaderboard_mode: false,
          course_rounds: cfgCourseRounds.filter((cr) => cr.course_id && cr.rounds > 0),
          dynamic_scheduling: cfgDynamic,
          announce_chat_ids: cfgAnnounceChats,
          announce_batch_minutes: cfgBatchMinutes,
        }),
      });
      await load();
    } finally { setSaving(false); }
  };

  // ── Poll ───────────────────────────────────────────────────────────────────
  const addSlot = () => {
    if (!newSlotDate) return;
    if (startDate && endDate) {
      const lo = startDate.length >= 10 ? startDate.slice(0, 10) : startDate;
      const hi = endDate.length >= 10 ? endDate.slice(0, 10) : endDate;
      if (newSlotDate < lo || newSlotDate > hi) {
        alert(`Date must be within the tournament window (${fmtDate(startDate)} → ${fmtDate(endDate)}).`);
        return;
      }
    }
    if (!newSlots.some((s) => s.slot_date === newSlotDate && s.am_pm === newSlotAmPm))
      setNewSlots([...newSlots, { slot_date: newSlotDate, am_pm: newSlotAmPm }]);
  };

  const deleteExistingSlot = async (slotId: number) => {
    if (!window.confirm('Delete this time slot? Any existing votes for it will also be removed.')) return;
    setSaving(true);
    try {
      await authFetch(`${APIURL}/tournaments/${tournamentId}/scheduler/poll/slots/${slotId}`, { method: 'DELETE' });
      await load();
    } finally { setSaving(false); }
  };

  // Save the invite list. A player unticked here leaves the poll even if they
  // have answered (their picks are kept server-side). Under dynamic scheduling
  // the server rebuilds the schedule right after answering, so reload once
  // more a few seconds later to show the rebuilt schedule.
  const saveInvitations = async () => {
    const res = await authFetch(`${APIURL}/tournaments/${tournamentId}/scheduler/poll/invitations`, { method: 'POST', body: JSON.stringify({ player_ids: inviteIds }) });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) { alert(body.error || 'Failed to save invitations.'); return; }
    const rosterChanged = ((body.added?.length || 0) + (body.removed?.length || 0)) > 0;
    if (rosterChanged && data?.config?.dynamic_scheduling) {
      setTimeout(() => { load({ silent: true }); loadDynamicStatus(); }, 5000);
    }
  };

  const savePoll = async () => {
    const slots = [...(data?.poll?.slots.map((s) => ({ slot_date: s.slot_date, am_pm: s.am_pm })) || []), ...newSlots];
    if (slots.length === 0) return alert('Add at least one time slot.');
    setSaving(true);
    try {
      await authFetch(`${APIURL}/tournaments/${tournamentId}/scheduler/poll`, { method: 'POST', body: JSON.stringify({ slots }) });
      setNewSlots([]);
      await saveInvitations();
      await load();
    } finally { setSaving(false); }
  };

  const autoPopulatePoll = async () => {
    const dates = datesBetween(startDate, endDate);
    if (dates.length === 0) { alert('Tournament needs a start date and end date to auto-generate the poll.'); return; }
    const existing = new Set((data?.poll?.slots || []).map((s) => `${s.slot_date}|${s.am_pm}`));
    const generated: { slot_date: string; am_pm: 'AM' | 'PM' }[] = [];
    for (const d of dates) for (const ap of ['AM', 'PM'] as const) if (!existing.has(`${d}|${ap}`)) generated.push({ slot_date: d, am_pm: ap });
    if (generated.length === 0) { alert('All date/slot combinations already exist in the poll.'); return; }
    const slots = [...(data?.poll?.slots.map((s) => ({ slot_date: s.slot_date, am_pm: s.am_pm })) || []), ...generated];
    setSaving(true);
    try {
      await authFetch(`${APIURL}/tournaments/${tournamentId}/scheduler/poll`, { method: 'POST', body: JSON.stringify({ slots }) });
      await saveInvitations();
      await load();
    } finally { setSaving(false); }
  };

  const togglePlayerSlotAndSave = async (inv: PollInvitation, slotId: number) => {
    const current = playerDraftSlots[inv.player_id] ?? (inv.voted_slot_ids || []).map(Number);
    const newSlots = current.includes(slotId)
      ? current.filter((id) => id !== slotId)
      : [...current, slotId];
    setPlayerDraftSlots((prev) => ({ ...prev, [inv.player_id]: newSlots }));
    setPlayerSaving(inv.player_id);
    try {
      await authFetch(`${APIURL}/tournaments/${tournamentId}/scheduler/poll/vote`, {
        method: 'POST',
        body: JSON.stringify({ slot_ids: newSlots, notes: inv.notes || null, behalf_player_id: inv.player_id }),
      });
    } catch {
      // Revert optimistic update on failure
      setPlayerDraftSlots((prev) => {
        const next = { ...prev };
        delete next[inv.player_id];
        return next;
      });
    } finally {
      setPlayerSaving(null);
    }
  };

  const setPollStatus = async (status: 'open' | 'closed') => {
    setSaving(true);
    try {
      await authFetch(`${APIURL}/tournaments/${tournamentId}/scheduler/poll/status`, { method: 'PATCH', body: JSON.stringify({ status }) });
      await load();
    } finally { setSaving(false); }
  };

  // ── PDF export ─────────────────────────────────────────────────────────────
  const downloadPdf = () => {
    window.print();
  };

  const openWaModal = async () => {
    setWaError(null);
    setWaCaption(`${tournamentName || 'Tournament'} — Schedule`);
    setWaModal(true);
    try {
      const token = await getAccessTokenSilently();
      const res = await fetch(`${APIURL}/whatsapp/chats`, { headers: { Authorization: `Bearer ${token}` } });
      const groups = await res.json();
      setWaGroups(groups || []);
      if (groups?.length) setWaGroupId(groups[0].id);
    } catch { setWaError('Could not load WhatsApp groups. Is the bot connected?'); }
  };

  const sendPdfViaWhatsApp = async () => {
    if (!waGroupId) return;
    setWaSending(true);
    setWaError(null);
    try {
      const token = await getAccessTokenSilently();
      const res = await fetch(`${APIURL}/tournaments/${tournamentId}/scheduler/send-pdf-whatsapp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ jid: waGroupId, caption: waCaption }),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.error); }
      setWaModal(false);
    } catch (e: any) { setWaError(e.message || 'Failed to send.'); }
    finally { setWaSending(false); }
  };

  // ── Rounds ─────────────────────────────────────────────────────────────────
  const updateRoundAssignments = (updated: typeof roundAssignments) => {
    hasRoundsChange.current = true;
    setRoundAssignments(updated);
  };

  const addReservationToRound = (idx: number, resId: number) => {
    if (!resId) return;
    if (roundAssignments[idx].reservation_ids.includes(resId)) return;
    const updated = [...roundAssignments];
    const slotId = roundAssignments[idx].slot_id;
    updated[idx] = { ...updated[idx], reservation_ids: [...updated[idx].reservation_ids, resId] };
    updateRoundAssignments(updated);
    setShowAddResvRow((prev) => { const next = { ...prev }; delete next[slotId]; return next; });
  };

  const removeReservationFromRound = (idx: number, resId: number) => {
    const updated = [...roundAssignments];
    updated[idx] = { ...updated[idx], reservation_ids: updated[idx].reservation_ids.filter((id) => id !== resId) };
    updateRoundAssignments(updated);
  };

  // ── Schedule ───────────────────────────────────────────────────────────────
  const generateSchedule = async () => {
    setSaving(true);
    try {
      // Persist the current Rounds-tab state before generating. The Rounds tab
      // is rendered from the poll slots in memory and only auto-saves when a
      // field is edited, so a freshly-set-up tournament can have rounds showing
      // on screen that were never written to the DB — which made the generator
      // fail with "No events configured." Flushing here makes what you see the
      // exact set of events that gets scheduled.
      if (roundAssignments.length === 0) {
        return alert('No date/time slots to schedule yet — add slots to the poll first.');
      }
      const put = await authFetch(`${APIURL}/tournaments/${tournamentId}/scheduler/rounds`, {
        method: 'PUT',
        body: JSON.stringify({ rounds: roundAssignments }),
      });
      if (!put.ok) {
        const perr = await put.json().catch(() => ({}));
        return alert(perr.error || 'Failed to save rounds before generating.');
      }
      hasRoundsChange.current = false; // the latest Rounds state is now persisted

      const r = await authFetch(`${APIURL}/tournaments/${tournamentId}/scheduler/generate`, { method: 'POST' });
      const result = await r.json();
      if (!r.ok) return alert(result.error || 'Generation failed.');
      await load();
    } finally { setSaving(false); }
  };

  // Mark which courses are unavailable for one date/session. The scheduler
  // builds around them; under dynamic scheduling this also triggers a rebuild
  // and an announcement naming the restriction as the reason.
  const saveCourseBlocks = async (slotId: number, courseIds: number[]) => {
    setSaving(true);
    try {
      const res = await authFetch(`${APIURL}/tournaments/${tournamentId}/scheduler/slots/${slotId}/course-blocks`, {
        method: 'PUT',
        body: JSON.stringify({ course_ids: courseIds }),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        alert(b.error || 'Failed to save course availability.');
        return;
      }
      await load({ silent: true });
    } finally { setSaving(false); }
  };

  const validateSchedule = async () => {
    setDiagLoading(true);
    try {
      const r = await authFetch(`${APIURL}/tournaments/${tournamentId}/scheduler/validate`, { method: 'POST' });
      const result = await r.json();
      if (!r.ok) return alert(result.error || 'Validation failed.');
      await load({ silent: true });
    } finally { setDiagLoading(false); }
  };

  const publishSchedule = async (status: 'draft' | 'published') => {
    setSaving(true);
    try {
      await authFetch(`${APIURL}/tournaments/${tournamentId}/scheduler/publish`, { method: 'PATCH', body: JSON.stringify({ status }) });
      await load();
    } finally { setSaving(false); }
  };

  const assignGroupReservation = async (groupId: number, reservationId: number | null) => {
    // Optimistically update local state so other dropdowns filter immediately.
    setLocalGroupResvs((prev) => ({ ...prev, [groupId]: reservationId }));
    try {
      const res = await authFetch(`${APIURL}/tournaments/${tournamentId}/scheduler/groups/${groupId}/reservation`, {
        method: 'PATCH',
        body: JSON.stringify({ reservation_id: reservationId }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        // Revert optimistic update on failure.
        setLocalGroupResvs((prev) => { const next = { ...prev }; delete next[groupId]; return next; });
        if (res.status === 404) {
          // The schedule was rebuilt since this page loaded, so this group is
          // gone. Reload rather than leaving the admin staring at a stale board.
          await load({ silent: true });
          alert('This schedule was rebuilt since the page loaded, so that group no longer exists. '
            + 'The schedule has been refreshed — please pick the tee time again.');
          return;
        }
        alert(body.error || 'Failed to assign reservation.');
        return;
      }
      await load({ silent: true });
      // Clear local override — server data is now authoritative.
      setLocalGroupResvs((prev) => { const next = { ...prev }; delete next[groupId]; return next; });
    } catch (e) {
      setLocalGroupResvs((prev) => { const next = { ...prev }; delete next[groupId]; return next; });
      console.error('[assign group reservation]', e);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────

  if (loading) return <div style={styles.loading}>Loading scheduler…</div>;

  const poll = data?.poll;
  const schedule = data?.schedule;
  // Driven by the saved config, so the schedule tab reflects what the server is
  // actually doing rather than an unsaved toggle in the Setup form.
  const isDynamic = !!data?.config?.dynamic_scheduling;
  const announceBatchMinutes = Number(data?.config?.announce_batch_minutes ?? 15);
  const pollSlots = poll?.slots || [];
  const standings = data?.tournament_standings || [];

  const configuredRounds = (data?.rounds || []);

  // The Schedule tab's group dropdowns must reflect the latest links from the
  // Rounds tab, including unsaved edits. Build round_number → reservation IDs
  // from the live `roundAssignments` state (keyed by slot_id) using the saved
  // slot→round_number mapping. Fall back to the persisted reservations on any
  // round we don't have in local state.
  const slotToRound: Record<number, number> = {};
  for (const r of configuredRounds) {
    if (r.slot_id != null) slotToRound[r.slot_id] = r.round_number;
  }
  const linkedResvIdsByRound: Record<number, number[]> = {};
  for (const ra of roundAssignments) {
    const rnum = slotToRound[ra.slot_id];
    if (rnum != null) linkedResvIdsByRound[rnum] = ra.reservation_ids.map(Number);
  }
  for (const r of configuredRounds) {
    if (linkedResvIdsByRound[r.round_number] != null) continue;
    linkedResvIdsByRound[r.round_number] = (r.reservations || []).map((res) => Number(res.reservation_id));
  }

  // Per-day set of already-assigned reservation IDs. Uses local optimistic
  // overrides so dropdowns update instantly when a selection is made.
  const assignedByDate: Record<string, Set<number>> = {};
  for (const g of (schedule?.groups || [])) {
    if (!g.slot_date) continue;
    const resId = Object.prototype.hasOwnProperty.call(localGroupResvs, g.group_id)
      ? localGroupResvs[g.group_id]
      : g.reservation_id;
    if (resId == null) continue;
    const date = g.slot_date.slice(0, 10);
    if (!assignedByDate[date]) assignedByDate[date] = new Set();
    assignedByDate[date].add(Number(resId));
  }

  const groupsByRound: Record<number, ScheduleGroup[]> = {};
  for (const g of schedule?.groups || []) {
    if (!groupsByRound[g.round_number]) groupsByRound[g.round_number] = [];
    groupsByRound[g.round_number].push(g);
  }

  // Course distribution validation
  const courseRoundsSum = cfgCourseRounds.reduce((s, cr) => s + (cr.rounds || 0), 0);
  const courseRoundsValid = cfgCourseRounds.length === 0 || courseRoundsSum === cfgTotalRounds;

  return (
    <div style={styles.panel}>
      {/* Tab bar */}
      <div style={styles.tabBar} className="no-print">
        {(['config', 'poll', 'rounds', 'schedule'] as const).map((t) => (
          <button key={t} style={{ ...styles.tabBtn, ...(activeTab === t ? styles.tabActive : {}) }} onClick={() => setActiveTab(t)}>
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {/* ── Config ─────────────────────────────────────────────────── */}
      {activeTab === 'config' && (
        <div style={styles.tabContent}>
          <h4 style={styles.sectionTitle}>Schedule Configuration</h4>

          <div style={styles.field}>
            <label style={styles.label}>Total Rounds</label>
            <select style={styles.select} value={cfgTotalRounds} onChange={(e) => setCfgTotalRounds(Number(e.target.value) as 2 | 3 | 4)}>
              <option value={2}>2</option><option value={3}>3</option><option value={4}>4</option>
            </select>
          </div>

          <div style={styles.field}>
            <label style={styles.checkLabel}>
              <input type="checkbox" checked={cfgMeetEveryone} onChange={(e) => setCfgMeetEveryone(e.target.checked)} />
              &nbsp; Each player plays with every other player at least once
            </label>
          </div>

          {/* Course distribution */}
          <h5 style={styles.subTitle}>Course Distribution</h5>
          <p style={styles.hint}>
            If the tournament spans multiple courses, specify how many rounds each player must complete at each venue.
            Leave empty for no course constraint. Totals must equal the round count above.
          </p>
          {cfgCourseRounds.map((cr, i) => (
            <div key={i} style={{ ...styles.row, marginBottom: 6 }}>
              <select
                style={{ ...styles.select, flex: 1 }}
                value={cr.course_id || ''}
                onChange={(e) => {
                  const updated = [...cfgCourseRounds];
                  updated[i] = { ...updated[i], course_id: Number(e.target.value) };
                  setCfgCourseRounds(updated);
                }}
              >
                <option value="">— select course —</option>
                {Object.entries(
                  allCourses.reduce((groups: Record<string, Course[]>, c) => {
                    const key = c.facility_name || 'Other';
                    (groups[key] = groups[key] || []).push(c);
                    return groups;
                  }, {})
                ).map(([facility, items]) => (
                  <optgroup key={facility} label={facility}>
                    {items.map((c) => <option key={c.course_id} value={c.course_id}>{c.course_name}</option>)}
                  </optgroup>
                ))}
              </select>
              <input
                type="number" min={1} max={cfgTotalRounds}
                style={{ ...styles.input, width: 60 }}
                value={cr.rounds}
                onChange={(e) => {
                  const updated = [...cfgCourseRounds];
                  updated[i] = { ...updated[i], rounds: Number(e.target.value) };
                  setCfgCourseRounds(updated);
                }}
              />
              <span style={{ fontSize: 12, color: '#6c757d' }}>rounds</span>
              <button style={styles.removeBtn} onClick={() => setCfgCourseRounds(cfgCourseRounds.filter((_, j) => j !== i))}>✕</button>
            </div>
          ))}
          <button style={styles.btnSecondarySmall} onClick={() => setCfgCourseRounds([...cfgCourseRounds, { course_id: 0, rounds: 1 }])}>
            + Add Course
          </button>
          {cfgCourseRounds.length > 0 && (
            <div style={{ marginTop: 6, fontSize: 12, color: courseRoundsValid ? '#155724' : '#dc3545', fontWeight: 600 }}>
              Total: {courseRoundsSum} / {cfgTotalRounds} rounds{courseRoundsValid ? ' ✓' : ' — must equal total rounds'}
            </div>
          )}

          <div style={{ ...styles.infoBox, marginTop: 12 }}>
            <strong>Leaderboard rounds</strong> are set per-round in the <em>Rounds</em> tab.
            <strong> Final Round</strong> enforcement ensures every player's last round lands at the designated slot.
          </div>

          {/* ── Dynamic scheduling ── */}
          <h5 style={styles.subTitle}>Dynamic Scheduling</h5>
          <p style={styles.hint}>
            Rebuild the schedule automatically whenever a player changes their availability, and
            announce the change on WhatsApp with the schedule PDF and a read-only link.
            Closing the poll locks the schedule — no further automatic rebuilds.
          </p>
          <label style={{ ...styles.checkLabel, marginBottom: 8 }}>
            <input
              type="checkbox"
              checked={cfgDynamic}
              onChange={(e) => {
                setCfgDynamic(e.target.checked);
                if (e.target.checked && announceChatOptions.length === 0) loadAnnounceChats();
              }}
            />
            &nbsp;Enable dynamic scheduling
          </label>

          {cfgDynamic && (
            <div style={{ marginBottom: 12 }}>
              <span style={styles.label}>Announce to WhatsApp groups</span>
              {announceChatsError && (
                <div style={{ fontSize: 12, color: '#dc3545', marginBottom: 6 }}>{announceChatsError}</div>
              )}

              {/* Selected groups as removable chips — compact however many there are. */}
              {cfgAnnounceChats.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
                  {cfgAnnounceChats.map((id) => (
                    <span key={id} style={styles.chatChip}>
                      {announceChatOptions.find((c) => c.id === id)?.name || id}
                      <button
                        type="button"
                        aria-label="Remove group"
                        style={styles.chatChipX}
                        onClick={() => setCfgAnnounceChats(cfgAnnounceChats.filter((x) => x !== id))}
                      >
                        ✕
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {/* One dropdown that adds a group, rather than a checkbox per group. */}
              <select
                style={{ ...styles.select, width: '100%', maxWidth: 380, marginRight: 0 }}
                value=""
                onFocus={() => { if (announceChatOptions.length === 0) loadAnnounceChats(); }}
                onChange={(e) => {
                  const id = e.target.value;
                  if (id && !cfgAnnounceChats.includes(id)) setCfgAnnounceChats([...cfgAnnounceChats, id]);
                }}
              >
                <option value="">
                  {announceChatOptions.length === 0 ? 'Click to load WhatsApp groups…' : '+ Add a group…'}
                </option>
                {announceChatOptions
                  .filter((c) => !cfgAnnounceChats.includes(c.id))
                  .map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>

              {cfgAnnounceChats.length === 0 && announceChatOptions.length > 0 && (
                <div style={{ fontSize: 12, color: '#856404', marginTop: 4 }}>
                  No group selected — the schedule will still rebuild, but nothing will be announced.
                </div>
              )}

              <div style={{ marginTop: 10 }}>
                <span style={styles.label}>Batch announcements</span>
                <select
                  style={{ ...styles.select, maxWidth: 260 }}
                  value={cfgBatchMinutes}
                  onChange={(e) => setCfgBatchMinutes(Number(e.target.value))}
                >
                  <option value={0}>Send immediately</option>
                  <option value={5}>Every 5 minutes</option>
                  <option value={15}>Every 15 minutes</option>
                  <option value={30}>Every 30 minutes</option>
                  <option value={60}>Every hour</option>
                </select>
                <div style={{ fontSize: 11, color: '#6c757d', marginTop: 4 }}>
                  Availability changes made within the window are collected into one message.
                  The schedule itself always updates straight away.
                </div>
              </div>
            </div>
          )}

          {schedule?.public_url && (
            <div style={{ marginBottom: 12 }}>
              <span style={styles.label}>Read-only schedule link</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <a
                  href={schedule.public_url}
                  target="_blank"
                  rel="noreferrer"
                  style={{ fontSize: 12, overflowWrap: 'anywhere' }}
                >
                  {schedule.public_url}
                </a>
                <button
                  type="button"
                  style={styles.btnSecondarySmall}
                  onClick={() => {
                    navigator.clipboard?.writeText(schedule.public_url || '');
                    setLinkCopied(true);
                    setTimeout(() => setLinkCopied(false), 1800);
                  }}
                >
                  {linkCopied ? 'Copied ✓' : 'Copy'}
                </button>
              </div>
              <div style={{ fontSize: 11, color: '#6c757d', marginTop: 4 }}>
                Anyone with this link can view the schedule — no sign-in needed.
              </div>
            </div>
          )}

          <button style={styles.btn} onClick={saveConfig} disabled={saving || !courseRoundsValid}>
            {saving ? 'Saving…' : 'Save Config'}
          </button>
          {data?.config && (
            <div style={styles.savedBadge}>
              Saved: {data.config.total_rounds} rounds
              {data.config.meet_everyone_once ? ' • Meet everyone once ✓' : ''}
              {Array.isArray(data.config.course_rounds) && data.config.course_rounds.length > 0
                ? ` • ${data.config.course_rounds.map((cr) => {
                    const c = allCourses.find((x) => x.course_id === cr.course_id);
                    return `${c?.course_name ?? `#${cr.course_id}`}: ${cr.rounds}R`;
                  }).join(', ')}`
                : ''}
            </div>
          )}
        </div>
      )}

      {/* ── Poll ───────────────────────────────────────────────────── */}
      {activeTab === 'poll' && (
        <div style={styles.tabContent}>
          <h4 style={styles.sectionTitle}>Availability Poll</h4>

          {poll && (
            <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ ...styles.badge, background: poll.status === 'open' ? '#d4edda' : '#f8d7da', color: poll.status === 'open' ? '#155724' : '#721c24' }}>
                Poll {poll.status.toUpperCase()}
              </span>
              {poll.status === 'open'
                ? <button style={styles.btnSmall} onClick={() => setPollStatus('closed')} disabled={saving}>Close Poll</button>
                : <button style={styles.btnSmall} onClick={() => setPollStatus('open')} disabled={saving}>Reopen Poll</button>
              }
            </div>
          )}

          {pollSlots.length > 0 && (
            <>
              <h5 style={styles.subTitle}>My Availability</h5>
              <p style={styles.hint}>Click the Morning and/or Afternoon cell for each day you can play. Tap again to unselect.</p>
              <PollAvailabilityGrid
                slots={pollSlots}
                selectedSlotIds={myVotedSlots}
                disabled={poll?.status !== 'open' || saving}
                onToggle={(slotId) => {
                  hasUserVoteChange.current = true;
                  if (myVotedSlots.includes(slotId)) setMyVotedSlots(myVotedSlots.filter((id) => id !== slotId));
                  else setMyVotedSlots([...myVotedSlots, slotId]);
                }}
                onDeleteSlot={(slotId) => deleteExistingSlot(slotId)}
              />
              {poll?.status === 'open' && (
                <div style={{ ...styles.field, marginTop: 12 }}>
                  <label style={styles.label}>Notes / requirements (auto-saved)</label>
                  <textarea style={styles.textarea} value={myNotes}
                    onChange={(e) => { hasUserVoteChange.current = true; setMyNotes(e.target.value); }}
                    placeholder="e.g. Can only play mornings, need cart…" rows={2} />
                  <span style={{ fontSize: 11, color: '#6c757d' }}>Changes save automatically as you select.</span>
                </div>
              )}
            </>
          )}

          <hr style={styles.hr} />

          {startDate && endDate && (
            <div style={styles.infoBox}>
              <strong>📅 Tournament window:</strong> {fmtDate(startDate)} → {fmtDate(endDate)}
              {' '}({datesBetween(startDate, endDate).length} days, max {datesBetween(startDate, endDate).length * 2} AM/PM slots).
              <div style={{ marginTop: 8 }}>
                <button style={styles.btnSmall} onClick={autoPopulatePoll} disabled={saving}>
                  ⚡ Auto-generate poll from tournament dates
                </button>
              </div>
            </div>
          )}

          <h5 style={styles.subTitle}>Add Individual Time Slot</h5>
          <div style={styles.row}>
            <input type="date" style={styles.input} value={newSlotDate}
              min={startDate ? (startDate.length >= 10 ? startDate.slice(0, 10) : startDate) : undefined}
              max={endDate ? (endDate.length >= 10 ? endDate.slice(0, 10) : endDate) : undefined}
              onChange={(e) => setNewSlotDate(e.target.value)} />
            <select style={{ ...styles.select, minWidth: 130, flex: '0 0 auto' }} value={newSlotAmPm} onChange={(e) => setNewSlotAmPm(e.target.value as 'AM' | 'PM')}>
              <option value="AM">Morning (AM)</option>
              <option value="PM">Afternoon (PM)</option>
            </select>
            <button style={styles.btnSmall} onClick={addSlot}>+ Add</button>
          </div>
          {newSlots.map((s, i) => (
            <div key={i} style={styles.newSlotRow}>
              <span>{fmtDate(s.slot_date)} — {fmtSession(s.am_pm)}</span>
              <button style={styles.removeBtn} onClick={() => setNewSlots(newSlots.filter((_, idx) => idx !== i))}>✕</button>
            </div>
          ))}

          <h5 style={styles.subTitle}>
            Invite Players <span style={{ fontWeight: 400, color: '#6c757d', fontSize: 12 }}>({inviteIds.length} selected)</span>
          </h5>
          <p style={styles.hint}>
            Untick a player to take them out of the poll and out of every round still ahead — even after they have
            answered. Their availability picks stay on file and come back if you invite them again.
          </p>
          {(() => {
            // Picks a player would keep (or has kept) while out of the poll: what
            // the server still holds for someone already removed, or what an
            // invited player has answered so far if they are unticked now.
            const picksKeptById = new Map<number, number>();
            for (const inv of poll?.invitations || []) picksKeptById.set(inv.player_id, Number(inv.vote_count) || 0);
            for (const sv of poll?.saved_votes || []) picksKeptById.set(sv.player_id, Number(sv.vote_count) || 0);
            const visiblePlayers = allPlayers.filter((p) => {
              if (p.league_status === 'Removed') return false;
              if (inviteStatusFilter !== 'all' && p.league_status !== inviteStatusFilter) return false;
              if (inviteSearch.trim() && !`${p.first_name} ${p.last_name}`.toLowerCase().includes(inviteSearch.toLowerCase())) return false;
              return true;
            });
            const allMemberIds = allPlayers.filter((p) => p.league_status === 'Member').map((p) => p.player_id);
            const visibleIds = visiblePlayers.map((p) => p.player_id);
            const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => inviteIds.includes(id));
            return (
              <>
                <div style={styles.inviteFilters}>
                  <input type="text" style={{ ...styles.input, flex: '1 1 140px', minWidth: 0, marginRight: 0 }} placeholder="Search players…"
                    value={inviteSearch} onChange={(e) => setInviteSearch(e.target.value)} />
                  <select style={{ ...styles.select, flex: '0 0 auto', marginRight: 0 }} value={inviteStatusFilter} onChange={(e) => setInviteStatusFilter(e.target.value as any)}>
                    <option value="all">All types</option><option value="Member">Members</option><option value="Guest">Guests</option>
                  </select>
                </div>
                <div style={styles.bulkActions}>
                  <button style={styles.btnSmall} onClick={() => setInviteIds(Array.from(new Set([...inviteIds, ...allMemberIds])))}>+ Invite all Members</button>
                  <button style={styles.btnSecondarySmall}
                    onClick={() => setInviteIds(allVisibleSelected ? inviteIds.filter((id) => !visibleIds.includes(id)) : Array.from(new Set([...inviteIds, ...visibleIds])))}>
                    {allVisibleSelected ? '− Deselect visible' : '+ Select visible'}
                  </button>
                  <button style={styles.btnSecondarySmall} onClick={() => setInviteIds([])}>Clear</button>
                </div>
                <div style={styles.inviteList}>
                  {visiblePlayers.length === 0 && <div style={{ padding: 8, color: '#888', fontSize: 13 }}>No players match.</div>}
                  {visiblePlayers.map((p) => (
                    <label key={p.player_id} style={styles.inviteRow}>
                      <input type="checkbox" checked={inviteIds.includes(p.player_id)}
                        onChange={(e) => { if (e.target.checked) setInviteIds([...inviteIds, p.player_id]); else setInviteIds(inviteIds.filter((id) => id !== p.player_id)); }} />
                      <span style={{ flex: 1, marginLeft: 6 }}>
                        {p.first_name} {p.last_name}
                        {!inviteIds.includes(p.player_id) && (picksKeptById.get(p.player_id) || 0) > 0 && (
                          <span style={styles.picksKept} title="Out of the poll — these availability picks come back if you invite them again">
                            {' '}· {picksKeptById.get(p.player_id)} pick{picksKeptById.get(p.player_id) === 1 ? '' : 's'} kept
                          </span>
                        )}
                      </span>
                      <span style={{ ...styles.badge, background: p.league_status === 'Member' ? '#cfe2ff' : '#f8f9fa', color: p.league_status === 'Member' ? '#084298' : '#6c757d' }}>
                        {p.league_status || '—'}
                      </span>
                    </label>
                  ))}
                </div>
              </>
            );
          })()}

          <button style={styles.btn} onClick={savePoll} disabled={saving}>
            {saving ? 'Saving…' : poll ? 'Update Poll & Invitations' : 'Create Poll'}
          </button>

          {poll && poll.invitations.length > 0 && (
            <>
              <hr style={styles.hr} />
              <h5 style={styles.subTitle}>Player Responses</h5>
              <p style={styles.hint}>Selections save automatically when toggled.</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {poll.invitations.map((inv) => {
                  const effectiveSlots = playerDraftSlots[inv.player_id] ?? (inv.voted_slot_ids || []).map(Number);
                  const isSaving = playerSaving === inv.player_id;
                  return (
                    <div key={inv.player_id} style={{ background: '#fff', border: '1px solid #dee2e6', borderRadius: 6, overflow: 'hidden' }}>
                      {/* Header */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderBottom: '1px solid #f1f3f5' }}>
                        <span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{inv.first_name} {inv.last_name}</span>
                        {isSaving && <span style={{ fontSize: 11, color: '#6c757d' }}>Saving…</span>}
                        <span style={{ color: '#6c757d', fontSize: 12 }}>{effectiveSlots.length} slot{effectiveSlots.length !== 1 ? 's' : ''}</span>
                        <span style={{ ...styles.badge, background: inv.voted ? '#d4edda' : '#fff3cd', color: inv.voted ? '#155724' : '#856404' }}>
                          {inv.voted ? 'Voted' : 'Pending'}
                        </span>
                      </div>
                      {/* Always-on availability grid */}
                      <div style={{ padding: 10 }}>
                        <PollAvailabilityGrid
                          slots={pollSlots}
                          selectedSlotIds={effectiveSlots}
                          showVoteCounts={false}
                          onToggle={(slotId) => togglePlayerSlotAndSave(inv, slotId)}
                        />
                        {inv.notes && (
                          <div style={{ marginTop: 6, fontSize: 12, fontStyle: 'italic', color: '#495057' }}>📝 {inv.notes}</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Rounds ─────────────────────────────────────────────────── */}
      {activeTab === 'rounds' && (
        <div style={styles.tabContent}>
          <h4 style={styles.sectionTitle}>Events</h4>
          <p style={styles.hint}>
            Each Morning and Afternoon slot is its own event — a player available for both AM and PM on the same day can play two rounds.
            Link tee-time reservations and flag leaderboard/final rounds here.
          </p>

          {roundAssignments.length === 0 && (
            <div style={styles.warningBox}>⚠ No poll slots yet — create the poll first (Poll tab) to see events here.</div>
          )}

          {roundAssignments.map((ra, idx) => (
            <div key={ra.slot_id} style={styles.roundCard}>
              <div style={{ ...styles.row, flexWrap: 'wrap' }}>
                <span style={styles.roundLabel}>{fmtDate(ra.slot_date)}</span>
                <span style={{ ...styles.badge, background: ra.am_pm === 'AM' ? '#e7f1ff' : '#fff3cd', color: ra.am_pm === 'AM' ? '#084298' : '#856404', fontWeight: 700 }}>
                  {fmtSession(ra.am_pm)}
                </span>
                <span style={{ ...styles.badge, background: '#f1f3f5', color: '#495057' }}>
                  {ra.vote_count} {ra.vote_count === 1 ? 'vote' : 'votes'}
                </span>
                <select style={styles.select} value={ra.course_id ?? ''}
                  onChange={(e) => { const u = [...roundAssignments]; u[idx] = { ...u[idx], course_id: e.target.value ? Number(e.target.value) : null }; updateRoundAssignments(u); }}>
                  <option value="">— course (optional) —</option>
                  {Object.entries(
                    allCourses.reduce((groups: Record<string, Course[]>, c) => {
                      const key = c.facility_name || 'Other';
                      (groups[key] = groups[key] || []).push(c);
                      return groups;
                    }, {})
                  ).map(([facility, items]) => (
                    <optgroup key={facility} label={facility}>
                      {items.map((c) => <option key={c.course_id} value={c.course_id}>{c.course_name}</option>)}
                    </optgroup>
                  ))}
                </select>
                <label style={styles.checkLabel}>
                  <input type="checkbox" checked={ra.is_leaderboard_round}
                    onChange={(e) => { const u = [...roundAssignments]; u[idx] = { ...u[idx], is_leaderboard_round: e.target.checked }; updateRoundAssignments(u); }} />
                  &nbsp;🏆 Leaderboard
                </label>
                <label style={styles.checkLabel}>
                  <input type="checkbox" checked={ra.is_final_round}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      updateRoundAssignments(roundAssignments.map((r, i) => ({ ...r, is_final_round: i === idx ? checked : false })));
                    }} />
                  &nbsp;🏁 Final Round
                </label>
              </div>

              {/* Courses that cannot be played this session, whatever the reason.
                  The scheduler builds around these instead of proposing a round
                  nobody can play. */}
              {(() => {
                const savedRound = configuredRounds.find((r) => r.slot_id === ra.slot_id);
                const blocked = (savedRound?.blocked_courses || []).map((b) => b.course_id);
                const planCourses = cfgCourseRounds
                  .map((cr) => allCourses.find((c) => c.course_id === cr.course_id))
                  .filter(Boolean) as Course[];
                // Only the courses this tournament actually plays are worth
                // restricting; anything else could never be scheduled anyway.
                const options = planCourses.length > 0 ? planCourses : allCourses;
                if (options.length === 0) return null;
                return (
                  <div style={{ marginTop: 8 }}>
                    <span style={styles.label}>Course unavailable this session</span>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                      {options.map((c) => (
                        <label key={c.course_id} style={styles.checkLabel}>
                          <input
                            type="checkbox"
                            checked={blocked.includes(c.course_id)}
                            disabled={saving}
                            onChange={(e) => {
                              const next = e.target.checked
                                ? [...blocked, c.course_id]
                                : blocked.filter((id) => id !== c.course_id);
                              saveCourseBlocks(ra.slot_id, next);
                            }}
                          />
                          &nbsp;🚫 {c.course_name}
                        </label>
                      ))}
                    </div>
                    {blocked.length > 0 && (
                      <div style={{ fontSize: 11, color: '#856404', marginTop: 4 }}>
                        Currently unable to reserve{' '}
                        {(savedRound?.blocked_courses || []).map((b) => b.course_name).join(', ')} on this
                        day and time. The schedule will be regenerated with this constraint.
                      </div>
                    )}
                  </div>
                );
              })()}

              <div style={{ marginTop: 8 }}>
                <span style={styles.label}>Linked Reservations</span>
                {ra.reservation_ids.length === 0 && !showAddResvRow[ra.slot_id] && (
                  <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>None linked yet.</div>
                )}
                {ra.reservation_ids.map((resId) => {
                  const r = reservations.find((rv) => rv.reservation_id === resId);
                  return (
                    <div key={resId} style={styles.resvTag}>
                      <span>{fmtDate(r?.reservation_date)} {r?.tee_time || ''} • {r?.course_name || `#${resId}`}{r?.num_players ? ` (${r.num_players}p)` : ''}</span>
                      <button style={styles.removeBtn} onClick={() => removeReservationFromRound(idx, resId)}>✕</button>
                    </div>
                  );
                })}
                {showAddResvRow[ra.slot_id] ? (
                  <div style={{ ...styles.row, marginTop: 4 }}>
                    <select
                      autoFocus
                      style={{ ...styles.select, flex: 1 }}
                      defaultValue=""
                      onChange={(e) => {
                        if (e.target.value) addReservationToRound(idx, Number(e.target.value));
                      }}
                    >
                      <option value="">— select reservation —</option>
                      {reservations.filter((rv) => {
                        // A reservation can only be linked once across all rounds.
                        const linkedAnywhere = roundAssignments.some((r) => r.reservation_ids.includes(rv.reservation_id));
                        if (linkedAnywhere) return false;
                        // Only reservations on this round's day can be linked.
                        if (!rv.reservation_date || !ra.slot_date) return false;
                        return rv.reservation_date.slice(0, 10) === ra.slot_date.slice(0, 10);
                      }).map((rv) => (
                        <option key={rv.reservation_id} value={rv.reservation_id}>
                          {fmtDate(rv.reservation_date)} {rv.tee_time || ''} • {rv.course_name || '—'}{rv.num_players ? ` (${rv.num_players}p)` : ''}
                        </option>
                      ))}
                    </select>
                    <button
                      style={styles.removeBtn}
                      onClick={() => setShowAddResvRow((prev) => { const next = { ...prev }; delete next[ra.slot_id]; return next; })}
                    >✕</button>
                  </div>
                ) : (
                  <button
                    style={{ ...styles.btnSecondarySmall, marginTop: 4 }}
                    onClick={() => setShowAddResvRow((prev) => ({ ...prev, [ra.slot_id]: true }))}
                  >
                    + Add Link
                  </button>
                )}
              </div>
            </div>
          ))}

          {roundAssignments.length > 0 && (
            <div style={{ fontSize: 12, color: '#6c757d', marginTop: 8 }}>
              {roundsSaving ? 'Saving…' : 'Changes save automatically.'}
            </div>
          )}
        </div>
      )}

      {/* ── Schedule ───────────────────────────────────────────────── */}
      {activeTab === 'schedule' && (
        <div style={styles.tabContent}>
          <h4 style={styles.sectionTitle}>Generated Schedule</h4>

          {isDynamic && (
            <div className="no-print" style={{ ...styles.infoBox, background: '#e8f5e9', border: '1px solid #a5d6a7', color: '#1b5e20' }}>
              <strong>⚡ Dynamic scheduling is on.</strong> This schedule is live and rebuilds itself
              whenever a player changes their availability — there is nothing to generate or publish.
              Changes are announced on WhatsApp
              {announceBatchMinutes > 0 ? ` in batches every ${announceBatchMinutes} min` : ' immediately'}.
              Closing the poll locks it.
            </div>
          )}

          {isDynamic && dynStatus && (
            <div className="no-print" style={styles.dynPanel}>
              <div style={styles.dynRow}>
                <span style={{
                  ...styles.badge,
                  background: dynStatus.locked ? '#e9ecef' : '#d4edda',
                  color: dynStatus.locked ? '#495057' : '#155724',
                }}>
                  {dynStatus.locked ? '🔒 Locked (poll closed)' : '⚡ Live'}
                </span>
                <span style={styles.dynMuted}>
                  Last rebuilt: {dynStatus.last_generated_at
                    ? new Date(dynStatus.last_generated_at).toLocaleString()
                    : 'never'}
                </span>
                {dynStatus.today && (
                  <span
                    style={styles.dynMuted}
                    title={`Rounds dated before this are locked. Timezone: ${dynStatus.timezone}`}
                  >
                    Today: {dynStatus.today} ({dynStatus.timezone})
                  </span>
                )}
                <span style={{
                  ...styles.badge,
                  background: dynStatus.whatsapp_connected ? '#d1e7dd' : '#f8d7da',
                  color: dynStatus.whatsapp_connected ? '#0a3622' : '#842029',
                }}>
                  WhatsApp {dynStatus.whatsapp_connected ? 'connected' : 'disconnected'}
                </span>
                {dynStatus.announce_chat_count === 0 && (
                  <span style={{ ...styles.badge, background: '#fff3cd', color: '#856404' }}>
                    No group selected
                  </span>
                )}
                <div style={{ flex: 1 }} />
                <button style={styles.btnSecondarySmall} onClick={loadDynamicStatus} type="button">
                  ↻ Refresh
                </button>
              </div>

              {dynStatus.pending_batch ? (
                <div style={styles.dynPending}>
                  <strong>
                    {dynStatus.pending_batch.change_count} change
                    {dynStatus.pending_batch.change_count === 1 ? '' : 's'} collected
                  </strong>
                  {' — due to send '}
                  {new Date(dynStatus.pending_batch.fires_at).toLocaleTimeString()}
                  <ul style={styles.dynList}>
                    {dynStatus.pending_batch.changes.map((c, i) => (
                      <li key={i}>{c.playerName}: {c.clause}</li>
                    ))}
                  </ul>
                  <button
                    style={{ ...styles.btn, background: '#25D366', border: 'none' }}
                    onClick={announceNow}
                    disabled={dynBusy}
                    type="button"
                  >
                    {dynBusy ? 'Sending…' : '📣 Send changes now'}
                  </button>
                </div>
              ) : (
                <div style={styles.dynMuted}>No changes waiting to be announced.</div>
              )}

              <details style={{ marginTop: 10 }}>
                <summary style={styles.dynSummary}>
                  Activity log ({dynStatus.activity.length})
                </summary>
                {dynStatus.activity.length === 0 && (
                  <div style={styles.dynMuted}>Nothing logged yet.</div>
                )}
                {dynStatus.activity.map((a) => (
                  <div key={a.activity_id} style={styles.dynLogRow}>
                    <div style={styles.dynLogHead}>
                      <span style={{
                        ...styles.badge,
                        background: a.status === 'failed' ? '#f8d7da'
                          : a.status === 'skipped' ? '#e9ecef' : '#d1e7dd',
                        color: a.status === 'failed' ? '#842029'
                          : a.status === 'skipped' ? '#495057' : '#0a3622',
                      }}>
                        {a.kind}
                      </span>
                      <span style={styles.dynMuted}>{new Date(a.created_at).toLocaleString()}</span>
                    </div>
                    {a.detail && <div style={{ fontSize: 12 }}>{a.detail}</div>}
                    {a.message && (
                      <details style={{ marginTop: 4 }}>
                        <summary style={styles.dynSummary}>WhatsApp message sent</summary>
                        <pre style={styles.dynPre}>{a.message}</pre>
                      </details>
                    )}
                  </div>
                ))}
              </details>
            </div>
          )}

          <div className="no-print" style={{ ...styles.row, flexWrap: 'wrap' as const, gap: 8 }}>
            {!isDynamic && (
              <button style={styles.btn} onClick={generateSchedule} disabled={saving || diagLoading}>
                {saving ? 'Generating…' : schedule ? 'Re-generate Schedule' : 'Generate Schedule'}
              </button>
            )}
            {schedule && (
              <button style={{ ...styles.btn, background: '#6f42c1' }} onClick={validateSchedule} disabled={saving || diagLoading}>
                {diagLoading ? 'Validating…' : 'Validate Schedule'}
              </button>
            )}
            {schedule && (
              <>
                {!isDynamic && (schedule.status === 'draft'
                  ? <button style={{ ...styles.btn, background: '#28a745' }} onClick={() => publishSchedule('published')} disabled={saving}>Publish</button>
                  : <button style={{ ...styles.btn, background: '#6c757d' }} onClick={() => publishSchedule('draft')} disabled={saving}>Unpublish</button>
                )}
                <span style={{ ...styles.badge, background: schedule.status === 'published' ? '#d4edda' : '#fff3cd', color: schedule.status === 'published' ? '#155724' : '#856404' }}>
                  {schedule.status.toUpperCase()}
                </span>
                <div style={{ flex: 1 }} />
                <button style={{ ...styles.btnSecondarySmall, fontSize: 13, padding: '6px 12px' }} onClick={downloadPdf}>
                  🖨 Print / Save PDF
                </button>
                <button style={{ ...styles.btnSecondarySmall, fontSize: 13, padding: '6px 12px', background: '#25D366', color: '#fff', borderColor: '#25D366' }} onClick={openWaModal}>
                  Send via WhatsApp
                </button>
              </>
            )}
          </div>

          {/* WhatsApp send modal */}
          {waModal && (
            <div style={styles.modalBackdrop} onClick={() => setWaModal(false)}>
              <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                <h5 style={{ margin: '0 0 12px 0', fontSize: 15 }}>Send Schedule via WhatsApp</h5>
                <label style={styles.label}>Group chat</label>
                {waGroups.length === 0 && !waError ? (
                  <div style={{ fontSize: 13, color: '#888', marginBottom: 10 }}>Loading groups…</div>
                ) : (
                  <select style={{ ...styles.select, width: '100%', marginBottom: 10, marginRight: 0 }} value={waGroupId} onChange={(e) => setWaGroupId(e.target.value)}>
                    {waGroups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                  </select>
                )}
                <label style={styles.label}>Message / caption</label>
                <textarea style={{ ...styles.textarea, marginBottom: 10 }} rows={3} value={waCaption} onChange={(e) => setWaCaption(e.target.value)} />
                {waError && <div style={{ color: '#dc3545', fontSize: 12, marginBottom: 8 }}>{waError}</div>}
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button style={styles.btnSecondarySmall} onClick={() => setWaModal(false)}>Cancel</button>
                  <button style={{ ...styles.btn, background: '#25D366', border: 'none' }} onClick={sendPdfViaWhatsApp} disabled={waSending || !waGroupId}>
                    {waSending ? 'Sending…' : 'Send PDF'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {schedule && (schedule.uninvited_players || []).length > 0 && (
            <div className="no-print" style={styles.warningBox}>
              ⚠ No longer in the poll but still scheduled:{' '}
              {(schedule.uninvited_players || [])
                .map((u) => `${u.first_name} ${u.last_name} (${u.events.join(', ')})`)
                .join('; ')}. Their tee-time seats are already released.{' '}
              {cfgDynamic
                ? 'Dynamic scheduling rebuilds the schedule without them shortly.'
                : 'Generate the schedule again to drop them.'}
            </div>
          )}

          {/* Schedule content — also used for print/PDF via window.print() */}
          <div ref={scheduleRef}>

          {schedule && schedule.warnings.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#495057', marginBottom: 6 }}>
                Schedule Diagnostics
                <span style={{ fontWeight: 400, color: '#adb5bd', marginLeft: 8 }}>
                  — click "Validate Schedule" to refresh after linking reservations
                </span>
              </div>
              {(['error', 'warning', 'info'] as const).map((sev) => {
                const items = schedule.warnings.filter((w) => w.severity === sev);
                if (items.length === 0) return null;
                const bg = sev === 'error' ? '#f8d7da' : sev === 'warning' ? '#fff3cd' : '#d1ecf1';
                const color = sev === 'error' ? '#721c24' : sev === 'warning' ? '#856404' : '#0c5460';
                const border = sev === 'error' ? '#f5c6cb' : sev === 'warning' ? '#ffc107' : '#bee5eb';
                const icon = sev === 'error' ? '✕' : sev === 'warning' ? '⚠' : 'ℹ';
                return (
                  <div key={sev} style={{ background: bg, border: `1px solid ${border}`, borderRadius: 4, padding: '8px 12px', marginBottom: 6 }}>
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {items.map((w, i) => (
                        <li key={i} style={{ color, fontSize: 12, marginBottom: i < items.length - 1 ? 4 : 0 }}>
                          {icon} {w.message}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── Tee Times Needed — responsive tiles ── */}
          {schedule && schedule.reservation_summary.length > 0 && (
            <>
              <h5 style={styles.subTitle}>Tee Times Needed</h5>
              <p style={styles.hint}>Book one tee-time reservation per tile. Assign it to specific groups below.</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
                {[...schedule.reservation_summary]
                  .sort((a, b) => {
                    const da = (a.date || '').slice(0, 10);
                    const dbb = (b.date || '').slice(0, 10);
                    if (da !== dbb) return da.localeCompare(dbb);
                    return (a.am_pm || '').localeCompare(b.am_pm || '');
                  })
                  .map((r, i) => (
                  <div key={i} style={styles.teeTile}>
                    <div style={{ fontSize: 13, fontWeight: 700 }}>{fmtDate(r.date)}</div>
                    <div style={{ marginTop: 4 }}>
                      <span style={{ ...styles.badge, background: r.am_pm === 'AM' ? '#e7f1ff' : '#fff3cd', color: r.am_pm === 'AM' ? '#084298' : '#856404' }}>
                        {fmtSession(r.am_pm)}
                      </span>
                    </div>
                    {r.course_name && (
                      r.is_suggested
                        ? <div style={{ marginTop: 4 }}><CourseChip name={r.course_name} compact /></div>
                        : <div style={{ marginTop: 4 }}><CourseChip name={r.course_name} confirmed compact /></div>
                    )}
                    <div style={{ fontSize: 22, fontWeight: 700, color: '#0d6efd', marginTop: 6, lineHeight: 1 }}>{r.group_count}</div>
                    <div style={{ fontSize: 11, color: '#6c757d' }}>tee time{r.group_count !== 1 ? 's' : ''}</div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ── Tournament Schedule heading ── */}
          {schedule && (
            <h5 style={{ ...styles.subTitle, marginTop: 20 }}>Tournament Schedule</h5>
          )}

          {/* ── Per-day schedule ── */}
          {schedule && (() => {
            const roundsByDate: Record<string, ScheduleRound[]> = {};
            for (const r of configuredRounds) {
              if (!r.slot_date) continue;
              if (!roundsByDate[r.slot_date]) roundsByDate[r.slot_date] = [];
              roundsByDate[r.slot_date].push(r);
            }
            const sortedDates = Object.keys(roundsByDate).sort();
            if (sortedDates.length === 0) return (
              <p style={{ color: '#888', marginTop: 12 }}>Configure events first (Rounds tab), then generate.</p>
            );
            return (
              <div className="schedule-cols">
              {sortedDates.map((date, dayIdx) => (
              <div key={date} className="schedule-day-col">
                <div className="schedule-day-col-header">Day {dayIdx + 1} — {fmtDate(date)}</div>
                <div className="schedule-day-sessions">
                {roundsByDate[date]
                  .sort((a, b) => (a.am_pm || '') < (b.am_pm || '') ? -1 : 1)
                  .map((roundInfo) => {
                    const isLeaderboard = !!roundInfo.is_leaderboard_round;
                    const isFinalRound = !!roundInfo.is_final_round;
                    // A date that has passed is history: regenerating leaves it
                    // exactly as it was, so say so rather than letting it look
                    // like the solver simply ignored it.
                    const isPlayed = !!roundInfo.slot_date
                      && roundInfo.slot_date < new Date().toISOString().slice(0, 10);
                    const groups = groupsByRound[roundInfo.round_number] || [];

                    return (
                      <div key={roundInfo.round_number} className="schedule-day-session">
                        {/* Session header */}
                        <div style={{ marginBottom: 8 }}>
                          <span style={{ ...styles.badge, background: roundInfo.am_pm === 'AM' ? '#e7f1ff' : '#fff3cd', color: roundInfo.am_pm === 'AM' ? '#084298' : '#856404', fontWeight: 700, fontSize: 12 }}>
                            {fmtSession(roundInfo.am_pm)}
                          </span>
                          {roundInfo.course_name && (
                            <div style={{ marginTop: 4 }}>
                              <CourseChip name={roundInfo.course_name} confirmed />
                            </div>
                          )}
                          {!roundInfo.course_name && roundInfo.suggested_course_name && (
                            <div style={{ marginTop: 4 }}>
                              <CourseChip name={roundInfo.suggested_course_name} />
                            </div>
                          )}
                          {(isLeaderboard || isFinalRound || isPlayed) && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                              {isPlayed && (
                                <span
                                  style={{ ...styles.badge, background: '#e9ecef', color: '#495057' }}
                                  title="This date has passed — regenerating keeps these groups as they were"
                                >
                                  🔒 Played
                                </span>
                              )}
                              {isLeaderboard && <span style={{ ...styles.badge, background: '#fff3cd', color: '#856404' }}>🏆 Leaderboard</span>}
                              {isFinalRound && <span style={{ ...styles.badge, background: '#d1e7dd', color: '#0a3622' }}>🏁 Final Round</span>}
                            </div>
                          )}
                        </div>

                        {isLeaderboard ? (
                          <div>
                            {/* Show proposed groups if the generator already has them */}
                            {groups.length > 0 ? (
                              <>
                                <div style={{ ...styles.infoBox, marginBottom: 10 }}>
                                  Suggested pairings based on current standings — groupings confirmed on the day with live scores.
                                </div>
                                <div style={styles.groupsRow}>
                                  {groups.map((g) => (
                                    <div key={g.group_id} style={styles.groupCard}>
                                      <div style={styles.groupCardTitle}>Group {g.group_number}</div>
                                      {(g.course_name || g.suggested_course_name) && (
                                        <div>
                                          <CourseChip
                                            name={(g.course_name || g.suggested_course_name) as string}
                                            confirmed={!!g.course_name}
                                          />
                                        </div>
                                      )}
                                      {g.tee_time && (
                                        <div style={styles.teeTimeBadge}>
                                          ⏰ {g.tee_time}
                                          {g.reservation_name ? ` — Reserved under ${g.reservation_name}` : ''}
                                        </div>
                                      )}
                                      {(g.complex_name || g.reservation_course_name) && (
                                        <div style={{ fontSize: 11, color: '#6c757d', marginBottom: 4 }}>
                                          {g.complex_name && <>at {g.complex_name}</>}
                                          {g.complex_name && g.reservation_course_name && ' · '}
                                          {g.reservation_course_name && <>{g.reservation_course_name}</>}
                                        </div>
                                      )}
                                      {g.players.map((p) => <div key={p.player_id} style={styles.playerName}>{p.first_name} {p.last_name}</div>)}
                                      {g.players.length <= 2 && (
                                        <div style={{ color: g.players.length === 1 ? '#dc3545' : '#856404', fontSize: 11, marginTop: 4 }}>
                                          {g.players.length === 1 ? '⚠ Solo' : '⚠ 2-some'}
                                        </div>
                                      )}
                                      <div style={{ marginTop: 8, borderTop: '1px solid #f1f3f5', paddingTop: 6 }}>
                                        <select
                                          style={{ ...styles.select, fontSize: 11, width: '100%', marginRight: 0 }}
                                          value={Object.prototype.hasOwnProperty.call(localGroupResvs, g.group_id) ? (localGroupResvs[g.group_id] ?? '') : (g.reservation_id ?? '')}
                                          onChange={(e) => assignGroupReservation(g.group_id, e.target.value ? Number(e.target.value) : null)}
                                        >
                                          <option value="">— tee time —</option>
                                          {(() => {
                                            const linkedIds = linkedResvIdsByRound[g.round_number] || [];
                                            const dateKey = (g.slot_date || '').slice(0, 10);
                                            const assignedForDate = assignedByDate[dateKey] || new Set<number>();
                                            return linkedIds
                                              .map((id) => reservations.find((rv) => Number(rv.reservation_id) === Number(id)))
                                              .filter((rv): rv is ReservationOption => !!rv)
                                              .filter((rv) => Number(rv.reservation_id) === Number(g.reservation_id) || !assignedForDate.has(Number(rv.reservation_id)))
                                              .map((rv) => <option key={rv.reservation_id} value={rv.reservation_id}>{rv.tee_time || '—'} · {rv.course_name || '—'}</option>);
                                          })()}
                                        </select>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </>
                            ) : (
                              <div style={styles.infoBox}>Groups will be formed on the day based on live standings.</div>
                            )}

                          </div>
                        ) : groups.length === 0 ? (
                          <div style={{ color: '#888', fontSize: 13 }}>No players scheduled for this session.</div>
                        ) : (
                          <div style={styles.groupsRow}>
                            {groups.map((g) => (
                              <div key={g.group_id} style={styles.groupCard}>
                                <div style={styles.groupCardTitle}>Group {g.group_number}</div>
                                {(g.course_name || g.suggested_course_name) && (
                                  <div>
                                    <CourseChip
                                      name={(g.course_name || g.suggested_course_name) as string}
                                      confirmed={!!g.course_name}
                                    />
                                  </div>
                                )}
                                {g.tee_time && (
                                  <div style={styles.teeTimeBadge}>
                                    ⏰ {g.tee_time}
                                    {g.reservation_name ? ` — Reserved under ${g.reservation_name}` : ''}
                                  </div>
                                )}
                                {(g.complex_name || g.reservation_course_name) && (
                                  <div style={{ fontSize: 11, color: '#6c757d', marginBottom: 4 }}>
                                    {g.complex_name && <>at {g.complex_name}</>}
                                    {g.complex_name && g.reservation_course_name && ' · '}
                                    {g.reservation_course_name && <>{g.reservation_course_name}</>}
                                  </div>
                                )}
                                {g.players.map((p) => <div key={p.player_id} style={styles.playerName}>{p.first_name} {p.last_name}</div>)}
                                {g.players.length <= 2 && (
                                  <div style={{ color: g.players.length === 1 ? '#dc3545' : '#856404', fontSize: 11, marginTop: 4 }}>
                                    {g.players.length === 1 ? '⚠ Solo' : '⚠ 2-some'}
                                  </div>
                                )}
                                <div style={{ marginTop: 8, borderTop: '1px solid #f1f3f5', paddingTop: 6 }}>
                                  <select
                                    style={{ ...styles.select, fontSize: 11, width: '100%', marginRight: 0 }}
                                    value={g.reservation_id ?? ''}
                                    onChange={(e) => assignGroupReservation(g.group_id, e.target.value ? Number(e.target.value) : null)}
                                  >
                                    <option value="">— tee time —</option>
                                    {(() => {
                                      const linkedIds = linkedResvIdsByRound[g.round_number] || [];
                                      const dateKey = (g.slot_date || '').slice(0, 10);
                                      const assignedForDate = assignedByDate[dateKey] || new Set<number>();
                                      return linkedIds
                                        .map((id) => reservations.find((rv) => Number(rv.reservation_id) === Number(id)))
                                        .filter((rv): rv is ReservationOption => !!rv)
                                        .filter((rv) => Number(rv.reservation_id) === Number(g.reservation_id) || !assignedForDate.has(Number(rv.reservation_id)))
                                        .map((rv) => <option key={rv.reservation_id} value={rv.reservation_id}>{rv.tee_time || '—'} · {rv.course_name || '—'}</option>);
                                    })()}
                                  </select>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              ))}
              </div>
            );
          })()}

          {/* ── Per-player schedule — responsive card grid ── */}
          {schedule && schedule.per_player_schedule && schedule.per_player_schedule.length > 0 && (
            <div style={{ marginTop: 24 }}>
              <h5 style={styles.subTitle}>Per-Player Schedule</h5>
              <p style={styles.hint}>Each player's rounds in chronological order. R1 = their first round in this tournament.</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {schedule.per_player_schedule.map((pp) => {
                  const target = data?.config?.total_rounds ?? 4;
                  const short = pp.rounds_assigned < target;
                  return (
                    <div key={pp.player_id} style={{ border: `1px solid ${short ? '#f5c6cb' : '#dee2e6'}`, borderRadius: 8, overflow: 'hidden' }}>
                      <div style={{ background: short ? '#fff5f5' : '#f8f9fa', padding: '8px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #dee2e6' }}>
                        <span style={{ fontWeight: 700, fontSize: 14 }}>{pp.first_name} {pp.last_name}</span>
                        <span style={{ fontSize: 12, fontWeight: 600, color: short ? '#dc3545' : '#28a745' }}>
                          {pp.rounds_assigned} / {target} rounds {short ? '⚠' : '✓'}
                        </span>
                      </div>
                      {pp.events.length === 0 ? (
                        <div style={{ padding: '8px 12px', color: '#888', fontSize: 13 }}>Not scheduled yet.</div>
                      ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 1, background: '#dee2e6' }}>
                          {pp.events.map((ev, i) => (
                            <div key={i} style={{ background: '#fff', padding: '10px 12px' }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: '#0d6efd', marginBottom: 3, letterSpacing: 0.5 }}>R{i + 1}</div>
                              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{fmtDate(ev.slot_date)}</div>
                              <span style={{ ...styles.badge, background: ev.am_pm === 'AM' ? '#e7f1ff' : '#fff3cd', color: ev.am_pm === 'AM' ? '#084298' : '#856404', fontSize: 10 }}>
                                {fmtSession(ev.am_pm)}
                              </span>
                              {(ev.course_name || ev.suggested_course_name) && (
                                <div style={{ marginTop: 4 }}>
                                  <CourseChip
                                    name={(ev.course_name || ev.suggested_course_name) as string}
                                    confirmed={!!ev.course_name}
                                    compact
                                  />
                                </div>
                              )}
                              {ev.tee_time && <div style={{ fontSize: 11, color: '#155724', marginTop: 3, fontWeight: 600 }}>⏰ {ev.tee_time}</div>}
                              {ev.reservation_name && (
                                <div style={{ fontSize: 10, color: '#6c757d', marginTop: 2 }}>
                                  Reserved under {ev.reservation_name}
                                </div>
                              )}
                              {ev.complex_name && (
                                <div style={{ fontSize: 10, color: '#adb5bd', marginTop: 1 }}>at {ev.complex_name}</div>
                              )}
                              {ev.is_leaderboard_round && <div style={{ fontSize: 10, color: '#856404', marginTop: 3 }}>🏆 Leaderboard</div>}
                              <div style={{ fontSize: 11, color: '#adb5bd', marginTop: 3 }}>Group {ev.group_number}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          </div>{/* end scheduleRef */}
        </div>
      )}
    </div>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  modalBackdrop: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  },
  modal: {
    background: '#fff', borderRadius: 10, padding: 24, width: '90%', maxWidth: 480,
    boxShadow: '0 8px 30px rgba(0,0,0,0.18)',
  },
  panel: { border: '1px solid #dee2e6', borderRadius: 6, background: '#fafafa', padding: 0, marginTop: 8 },
  loading: { padding: 16, color: '#888' },
  tabBar: { display: 'flex', borderBottom: '1px solid #dee2e6', background: '#f1f3f5', borderRadius: '6px 6px 0 0', flexWrap: 'wrap' },
  tabBtn: { border: 'none', background: 'transparent', padding: '8px 16px', cursor: 'pointer', fontSize: 13, color: '#495057', borderRadius: '6px 6px 0 0' },
  tabActive: { background: '#fff', borderBottom: '2px solid #0d6efd', color: '#0d6efd', fontWeight: 600 },
  tabContent: { padding: 16 },
  sectionTitle: { margin: '0 0 12px 0', fontSize: 15, fontWeight: 600 },
  subTitle: { margin: '12px 0 6px 0', fontSize: 13, fontWeight: 600, color: '#444' },
  hint: { color: '#6c757d', fontSize: 12, margin: '0 0 12px 0' },
  picksKept: { color: '#6c757d', fontSize: 11, fontStyle: 'italic' },
  field: { marginBottom: 12 },
  label: { display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4, color: '#495057' },
  checkLabel: { display: 'inline-flex', alignItems: 'center', fontSize: 13, cursor: 'pointer' },
  // maxWidth/minWidth keep a <select> from being widened past its container by a
  // long option label (e.g. "Club de Golf La Seigneurie"), which would push the
  // whole page wider than the centred layout allows and get clipped.
  select: { border: '1px solid #ced4da', borderRadius: 4, padding: '4px 8px', fontSize: 13, background: '#fff', marginRight: 8, maxWidth: '100%', minWidth: 0 },
  input: { border: '1px solid #ced4da', borderRadius: 4, padding: '4px 8px', fontSize: 13, marginRight: 8, maxWidth: '100%', minWidth: 0 },
  textarea: { width: '100%', border: '1px solid #ced4da', borderRadius: 4, padding: '4px 8px', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' },
  btn: { background: '#0d6efd', color: '#fff', border: 'none', borderRadius: 4, padding: '6px 14px', cursor: 'pointer', fontSize: 13 },
  btnSmall: { background: '#0d6efd', color: '#fff', border: 'none', borderRadius: 4, padding: '4px 10px', cursor: 'pointer', fontSize: 12 },
  btnSecondarySmall: { background: '#e9ecef', color: '#212529', border: '1px solid #ced4da', borderRadius: 4, padding: '4px 10px', cursor: 'pointer', fontSize: 12 },
  removeBtn: { background: 'transparent', border: 'none', color: '#dc3545', cursor: 'pointer', fontSize: 13 },
  savedBadge: { marginTop: 8, fontSize: 12, color: '#155724', background: '#d4edda', padding: '4px 8px', borderRadius: 4, display: 'inline-block' },
  badge: { display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 },
  teeTimeBadge: { display: 'inline-block', fontSize: 11, fontWeight: 700, color: '#155724', background: '#d1e7dd', borderRadius: 4, padding: '2px 7px', marginBottom: 4 },
  hr: { border: 'none', borderTop: '1px solid #dee2e6', margin: '12px 0' },
  row: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 8 },
  newSlotRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', fontSize: 13 },
  inviteFilters: { display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' },
  bulkActions: { display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' },
  inviteList: { border: '1px solid #dee2e6', borderRadius: 4, maxHeight: 240, overflowY: 'auto', background: '#fff', marginBottom: 12 },
  inviteRow: { display: 'flex', alignItems: 'center', padding: '6px 10px', fontSize: 13, borderBottom: '1px solid #f1f3f5', cursor: 'pointer' },
  roundCard: { border: '1px solid #dee2e6', borderRadius: 6, padding: '10px 12px', marginBottom: 12, background: '#fff' },
  roundLabel: { fontWeight: 600, fontSize: 13 },
  resvTag: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, background: '#f1f3f5', borderRadius: 4, padding: '4px 8px', marginBottom: 4, fontSize: 12, overflowWrap: 'anywhere' },
  roundBlock: { marginTop: 16 },
  roundBlockTitle: { margin: '0 0 10px 0', fontSize: 14, fontWeight: 700, color: '#212529', borderBottom: '1px solid #dee2e6', paddingBottom: 6 },
  groupsRow: { display: 'flex', flexDirection: 'column', gap: 8 },
  groupCard: { border: '1px solid #dee2e6', borderRadius: 6, padding: '10px 12px', background: '#fff' },
  groupCardTitle: { fontWeight: 700, fontSize: 12, color: '#0d6efd', marginBottom: 6 },
  playerName: { fontSize: 13, padding: '2px 0' },
  standingCard: { border: '1px solid', borderRadius: 6, padding: '8px 12px', minWidth: 130, textAlign: 'center' as const },
  teeTile: { border: '1px solid #dee2e6', borderRadius: 8, padding: '12px 14px', background: '#fff', minWidth: 130, flexShrink: 0 },
  dynPanel: {
    border: '1px solid #dee2e6', borderRadius: 6, background: '#fff',
    padding: '10px 12px', marginBottom: 12,
  },
  dynRow: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 },
  dynMuted: { fontSize: 12, color: '#6c757d' },
  dynPending: {
    background: '#fff8e1', border: '1px solid #ffe08a', borderRadius: 4,
    padding: '8px 10px', fontSize: 13,
  },
  dynList: { margin: '6px 0 8px', paddingLeft: 18, fontSize: 12 },
  dynSummary: { fontSize: 12, cursor: 'pointer', color: '#0d6efd' },
  dynLogRow: { borderTop: '1px solid #f1f3f5', padding: '6px 0' },
  dynLogHead: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 2 },
  dynPre: {
    background: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: 4,
    padding: 8, fontSize: 11, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', margin: '4px 0 0',
  },
  chatChip: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    background: '#e7f1ff', color: '#084298', border: '1px solid #b6d4fe',
    borderRadius: 12, padding: '2px 4px 2px 10px', fontSize: 12, maxWidth: '100%',
    overflowWrap: 'anywhere',
  },
  chatChipX: {
    border: 'none', background: 'transparent', color: '#084298',
    cursor: 'pointer', fontSize: 11, lineHeight: 1, padding: '2px 4px',
  },
  warningBox: { background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 4, padding: '8px 12px', fontSize: 13, margin: '10px 0' },
  infoBox: { background: '#e7f1ff', border: '1px solid #b6d4fe', borderRadius: 4, padding: '8px 12px', fontSize: 12, color: '#084298', marginBottom: 12, lineHeight: 1.45 },
};

export default TournamentSchedulerPanel;
